import React from 'react'
import { makeStyles } from '@material-ui/core/styles';
import { Accordion, AccordionDetails, AccordionSummary, Typography, Tooltip } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SimpleTableViewer from '../SimpleTable';
import Button from '@material-ui/core/Button';
import CardInfoViewer from '../CardInfo';

const useStyles = makeStyles((theme) => ({
    /* ===========================
       ACCORDION
    =========================== */
    accordionRoot: {
        '&::before': { display: 'none' },
        borderRadius: '4px 4px 0 0',
        marginBottom: '20px',
        border: '1px solid #EEE',
        // backgroundColor: '#FAFAFA',
        boxShadow: 'none',
        '&.Mui-expanded': { margin: 0 },
    },

    accordionDetails: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '70vh', // Set a maximum height
        overflowY: 'auto', // Enable vertical scrolling
        // padding: '16px 24px',
        '&::-webkit-scrollbar': {
            display: 'none !important',
        },
        // scrollbarWidth: 'none !important',
        msOverflowStyle: 'none !important',
    },

    expandedSummary: {
        /* xl baseline */
        '&.MuiAccordionSummary-root': { maxHeight: 60, minHeight: 60 },
        '&.Mui-expanded': { maxHeight: 60, minHeight: 60 },
        '& .MuiAccordionSummary-expandIcon': { marginRight: '5px !important' },
        '& .MuiAccordionSummary-expandIcon.Mui-expanded': { marginRight: '5px !important' },
        '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
    },

    /* ===========================
       CARD (copied from SummaryBlock, full width)
    =========================== */
    cardsColumn: {
        marginTop: 12,
        marginBottom: 12,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        [theme.breakpoints.down('md')]: { marginTop: 10, marginBottom: 10, gap: 10 },
        [theme.breakpoints.down('sm')]: { marginTop: 8, marginBottom: 8, gap: 8 },
    },
    cardRoot: {
        boxSizing: 'border-box',
        width: '100%',
        padding: 20,
        backgroundColor: 'var(--card-bg, #fff)',
        borderRadius: 4,
        border: '1px solid var(--card-border, #e5e7eb)',
        display: 'flex',
        flexDirection: 'row',
        gap: 12,
        minHeight: 96,
        transition: 'min-height .2s ease, padding .2s ease',
        [theme.breakpoints.down('md')]: { padding: 16, gap: 10, minHeight: 86 },
        [theme.breakpoints.down('sm')]: { padding: 12, gap: 8, minHeight: 74, borderRadius: 3 },
    },
    cardRootHasData: {
        minHeight: 128,
        [theme.breakpoints.down('md')]: { minHeight: 112 },
        [theme.breakpoints.down('sm')]: { minHeight: 92 },
    },
    cardIconWrap: {
        height: 36,
        width: 24,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: { height: 28, width: 18 },
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flex: 1,
        minHeight: 0,
    },
    cardTitle: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '21px',
        [theme.breakpoints.down('md')]: { fontSize: 13, lineHeight: '20px' },
        [theme.breakpoints.down('sm')]: { fontSize: 12, lineHeight: '18px' },
        flexShrink: 0,
    },
    cardText: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '21px',
        color: '#2B2C35',
        flex: 1,
        overflow: 'auto',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        paddingRight: 4,
        '&::-webkit-scrollbar': {
            display: 'none !important',
        },
        scrollbarWidth: 'none !important',
        msOverflowStyle: 'none !important',
        [theme.breakpoints.down('md')]: { fontSize: 13, lineHeight: '20px' },
        [theme.breakpoints.down('sm')]: { fontSize: 12, lineHeight: '18px', paddingRight: 2 },
    },
}));

const normalizeToSafeHtml = (value) => {
    if (value === null || value === undefined) return '';
    let html = String(value);
    html = html.replace(/\\n/g, '\n').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    html = html.replace(/\n/g, '<br/>');
    html = html
        .replace(/<\s*script[^>]*>[\s\S]*?<\s*\/\s*script\s*>/gi, '')
        .replace(/<\s*style[^>]*>[\s\S]*?<\s*\/\s*style\s*>/gi, '')
        .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, '')
        .replace(/javascript:/gi, '');
    return html;
};

const SimpleAccordionViewer = (props) => {
    const classes = useStyles();
    const { blockState, tableData, infoCardData, accordionHeader, AccordionDetailsHeader, formData, handleRemove, selectedDataElement, subDataElement, obligationManagementData, selectedDataElement1, selectedDataElement2, subDataElement1, linkedContractsdataElements, contract, isContractUnavailable } = props;

    const headerValue = (item, value) => {
        if (item[value]) {
            return typeof item?.[value] === 'object' ? item?.[value]?.value : item?.[value];
        }
        return '-';
    }

    const getCardValueFromItem = (item, cardConfig) => {
        const key = cardConfig?.value;
        if (!key) return cardConfig?.defaultText || '';
        const raw = item?.[key];
        console.log('SimpleAccordionViewer**cardValue', raw, cardConfig, item)
        const outputField = cardConfig?.outputField;
        const value = raw?.[outputField];
        return typeof raw === 'object' ? (value ?? raw?.value) : (raw ?? cardConfig?.defaultText ?? '');
    };

    const renderCard = (card, cardValue, index) => {
        const hasData = cardValue && cardValue !== card?.defaultText && String(cardValue).trim() !== '';
        const isHtml = !!card?.isHTML;
        const safeHtml = normalizeToSafeHtml(cardValue);
        return (
            <div
                key={`${card?.value}_${index}`}
                className={`${classes.cardRoot} ${hasData ? classes.cardRootHasData : ''}`}
                style={{
                    '--card-bg': card?.backgroundColor,
                    '--card-border': card?.borderColor,
                }}
            >
                <div className={classes.cardIconWrap}>
                    <img src={card?.icon} alt={card?.title} width={card?.title === "Obligation Language" ? 18 : 23} height={card?.title === "Obligation Language" ? 18 : 23} />
                </div>
                <div className={classes.cardBody}>
                    <Typography className={classes.cardTitle} style={{ color: card?.titleColor }}>
                        {card?.title}
                    </Typography>
                    <Typography className={classes.cardText} component="div">
                        {isHtml ? (
                            <span dangerouslySetInnerHTML={{ __html: safeHtml }} />
                        ) : (
                            <span style={{ whiteSpace: 'pre-line' }}>
                                {String(cardValue || '').replace(/\\n/g, '\n')}
                            </span>
                        )}
                    </Typography>
                </div>
            </div>
        );
    };

    const renderMultipleCards = (item) => {
        const multipleCard = contract?.multipleCard || [];
        const cards = multipleCard
            .map((mc, idx) => {
                const cardConfig = Object.values(mc || {})[0];
                if (!cardConfig?.enable || cardConfig?.type !== 'card') return null;
                const cardValue = getCardValueFromItem(item, cardConfig);
                return renderCard({ ...cardConfig, isHTML: true }, cardValue, idx);
            })
            .filter(Boolean);
        if (cards.length === 0) return null;
        return <div className={classes.cardsColumn}>{cards}</div>;
    };


    // const isButtonEnabled = isContractEnable || !props.enableWorkflow || (
    //     props.blockState?.['status'] !== 'Rejected' &&
    //     props.blockState?.['status'] !== 'Completed' &&
    //     props.blockState?.workflow &&
    //     props.blockState?.workflow?.currentRespondent?.id === props.members?._id
    // );

    const isButtonEnabled = !isContractUnavailable &&
        (
            !props.enableWorkflow ||
            (
                props.blockState?.status !== 'Rejected' &&
                props.blockState?.status !== 'Completed' &&
                props.blockState?.workflow &&
                props.blockState?.workflow?.currentRespondent?.id === props.members?._id
            )
        );

    console.log('isButtonEnabled**5666776', isButtonEnabled, isContractUnavailable)

    console.log('SimpleAccordionViewer**isButtonEnabled', isButtonEnabled, props.blockState, props.members, props.enableWorkflow, props.blockState, props.members?._id)
    const handleExport = (id, item) => {
        let payload = {
            dataModelId: contract?.uploadCard?.block?.dataModel,
            appid: props.match.params.appid,
            fields: contract?.obligationManagement?.selectedDataElement.map(i => i.value).join(",") + "," + contract?.clause?.selectedDataElement.map(i => i.value).join(",") + "," + linkedContractsdataElements.map(i => i.value).join(","),
            ids: [id],
            downloadType: 'excel',
            dataUniqueId: props.data.chartUniqueId + '_' + id + '_download',
        }
        props.downloadSheet(payload);
    }

    const createDocumentHierarchyArray = (hierarchyData, currentDocumentId) => {
        const result = [];

        // Find the current document and its relationships
        const findDocumentAndRelations = (node, parentNode = null) => {
            if (node?._id === currentDocumentId) {
                // Add parent first (if exists)
                if (parentNode) {
                    result.push({
                        ...parentNode,
                        relation: "Parent"
                    });
                }

                // Add siblings (other children of the same parent)
                if (parentNode && parentNode?.children) {
                    const siblings = parentNode.children.filter(child => child._id !== currentDocumentId);
                    siblings.forEach(sibling => {
                        result.push({
                            ...sibling,
                            relation: "Sibling"
                        });
                    });
                }

                // Add current document
                result.push({
                    ...node,
                    relation: "Current"
                });

                // Add children
                if (node?.children && node?.children?.length > 0) {
                    node.children.forEach(child => {
                        result.push({
                            ...child,
                            relation: "Child"
                        });
                    });
                }
            }

            // Recursively search in children
            if (node?.children && node?.children?.length > 0) {
                node.children.forEach(child => {
                    findDocumentAndRelations(child, node);
                });
            }
        };

        findDocumentAndRelations(hierarchyData);
        return result;
    };

    const handleOpenView = (item) => {
        const path = `/${props?.match?.params?.appid}/page/view/${item?.id}`;
        window.open(path, '_blank', 'noopener,noreferrer');
    }

    return (
        <React.Fragment>
            {
                tableData?.length > 0 && tableData?.map((item, index) => {
                    const documentHierarchyArray = createDocumentHierarchyArray(item?.[contract?.contractHierarchies?.name] ? item?.[contract?.contractHierarchies?.name] : {}, item?.id);
                    return (
                        <Accordion
                            elevation={0}
                            className={classes.accordionRoot}
                            defaultExpanded={contract?.accordionSupport?.defaultExpanded}
                        >
                            <AccordionSummary
                                className={classes.expandedSummary}
                                expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                            >
                                <div style={{ height: '40px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
                                    <div style={{ height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxSizing: 'border-box' }}>
                                        <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#5F6071', fontStyle: 'normal', lineHeight: '24px' }}>{`${contract?.accordionSupport?.accordionHeader?.label} :`}</Typography>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: '8px', boxSizing: 'border-box' }}>
                                            <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#2B2C35', fontStyle: 'normal', lineHeight: '24px' }}>{headerValue(item, contract?.accordionSupport?.accordionHeader?.value)}</Typography>
                                            {
                                                contract?.actions?.view?.enable && <img src={'/media/createStepper/openView.svg'} alt="download" style={{ width: '16px', height: '16px' }} onClick={() => { handleOpenView(item) }} />
                                            }
                                        </div>

                                    </div>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails className={`${classes.accordionDetails} myScrollContainer`}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingBottom: '20px' }}>
                                    <div style={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxSizing: 'border-box' }}>
                                        <div style={{ height: '40px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <div style={{ height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxSizing: 'border-box' }}>
                                                <div style={{ height: '20px', width: '2px', backgroundColor: '#3F51B5' }}>

                                                </div>
                                                <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#5F6071', fontStyle: 'normal', lineHeight: '24px' }}>{`${contract?.accordionSupport?.AccordionDetailsHeader?.label} :`}</Typography>
                                                <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#2B2C35', fontStyle: 'normal', lineHeight: '24px' }}>{headerValue(item, contract?.accordionSupport?.AccordionDetailsHeader?.value)}</Typography>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: '12px', boxSizing: 'border-box' }}>
                                            <div style={{ height: '35px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                                <Button
                                                    style={{
                                                        backgroundColor: !isButtonEnabled ? '#F5F5F5' : 'transparent',
                                                        color: isButtonEnabled ? '#3F51B5' : '#BDBDBD',
                                                        border: `1px solid ${isButtonEnabled ? '#3F51B5' : '#E0E0E0'}`,
                                                        borderRadius: '4px',
                                                        height: '35px',
                                                        padding: '12px',
                                                        boxSizing: 'border-box'
                                                    }}
                                                    onClick={() => { handleExport(item.id, item) }}
                                                    disabled={!isButtonEnabled}

                                                >
                                                    {contract?.actions?.export?.label}
                                                </Button>
                                            </div>
                                            <div style={{ height: '35px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                                <Button
                                                    style={{
                                                        color: isButtonEnabled ? '#3F51B5' : '#BDBDBD',
                                                        border: `1px solid ${isButtonEnabled ? '#3F51B5' : '#E0E0E0'}`,
                                                        borderRadius: '4px',
                                                        height: '35px',
                                                        padding: '12px',
                                                        boxSizing: 'border-box',
                                                        backgroundColor: !isButtonEnabled ? '#F5F5F5' : 'transparent',
                                                        cursor: !isButtonEnabled ? 'not-allowed' : 'pointer',
                                                        opacity: !isButtonEnabled ? 0.6 : 1
                                                    }}
                                                    disabled={!isButtonEnabled}
                                                    onClick={() => { handleRemove(item.id, item) }}
                                                >
                                                    {contract?.actions?.remove?.label}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                    {contract?.cardInformation?.enable && <CardInfoViewer {...props} data={contract?.cardInformation?.selectedDataElement} blockState={item?.["cardInformation"] ? item?.["cardInformation"] : {}} />}

                                    {Array.isArray(contract?.multipleCard) && contract.multipleCard.length > 0 && renderMultipleCards(item)}

                                    {contract?.obligationManagement?.enable && <SimpleTableViewer {...props} title={contract?.obligationManagement?.title} dataObj={item?.[contract?.obligationManagement?.name] ? item?.[contract?.obligationManagement?.name] : {}} selectedDataElement={contract?.obligationManagement?.selectedDataElement} dataModel="66e8297ca40acb5c5c38248a" selectedTableDataElement={contract?.obligationManagement?.subDataElement} isActionEnabled={contract?.obligationManagement?.actions?.enable} isObjectTable={contract?.obligationManagement?.actions?.isObjectTable} />}

                                    {contract?.clause?.enable && <SimpleTableViewer {...props} title={contract?.clause?.title} dataObj={item?.[contract?.clause?.name] ? item?.[contract?.clause?.name] : {}} selectedDataElement={contract?.clause?.selectedDataElement} dataModel="66e8297ca40acb5c5c38248a" selectedTableDataElement={contract?.clause?.subDataElement} isActionEnabled={contract?.clause?.actions?.enable} isObjectTable={contract?.clause?.actions?.isObjectTable} isGobalDownloadEnable={true} onGlobalDownload={() => { }} />}

                                    {contract?.contractHierarchies?.enable && <SimpleTableViewer {...props} title={contract?.contractHierarchies?.title} dataObj={documentHierarchyArray} selectedTableDataElement={contract?.contractHierarchies?.selectedDataElement} isActionEnabled={contract?.contractHierarchies?.actions?.enable} isObjectTable={contract?.contractHierarchies?.actions?.isObjectTable} isGobalDownloadEnable={true} onGlobalDownload={() => { }} />}
                                </div>

                            </AccordionDetails>
                        </Accordion>
                    )
                })
            }
            {
                tableData?.length === 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#5F6071', fontStyle: 'normal', lineHeight: '18px' }}>{contract?.noContracts}</Typography>
                    </div>
                )
            }
        </React.Fragment >
    )
}

export default SimpleAccordionViewer